import { safeDeleteSheet } from "@/lib/office-helpers";
import {
  ANSWER_CELLS,
  CATALOGO,
  CATALOGO_HEADERS,
  CONSULTAS,
  LAYOUT,
  PEDIDOS,
  PEDIDOS_HEADERS,
  PRECIOS_REGION,
  REGIONES,
  SHEETS,
  TRAMOS_COMISION,
  VENTAS_VENDEDORES,
  excelDate,
} from "../data/funciones/buscarx.data";

const ANSWER_FILL = "#FFF2CC";
const ANSWER_BORDER = "#E0B84F";

function writeTitle(ws: Excel.Worksheet, range: string, title: string, subtitle: string) {
  const [first] = range.split(":");
  ws.getRange(range).merge();
  ws.getRange(first).values = [[title]];
  ws.getRange(first).format.font.size = 20;
  ws.getRange(first).format.font.bold = true;
  ws.getRange(first).format.font.color = "#1E3F6D";
  const second = first.replace("1", "2");
  // Merged so autofit ignores the long subtitle when sizing column A.
  ws.getRange(range.replace(/1/g, "2")).merge();
  ws.getRange(second).values = [[subtitle]];
  ws.getRange(second).format.font.italic = true;
  ws.getRange(second).format.font.color = "#666666";
}

function markAnswer(ws: Excel.Worksheet, address: string) {
  const range = ws.getRange(address);
  range.format.fill.color = ANSWER_FILL;
  for (const edge of [
    Excel.BorderIndex.edgeTop,
    Excel.BorderIndex.edgeBottom,
    Excel.BorderIndex.edgeLeft,
    Excel.BorderIndex.edgeRight,
  ]) {
    range.format.borders.getItem(edge).style = Excel.BorderLineStyle.continuous;
    range.format.borders.getItem(edge).color = ANSWER_BORDER;
  }
}

function labelRow(ws: Excel.Worksheet, row: number, task: string, input: string | number) {
  ws.getRange(`A${row}:B${row}`).values = [[task, input]];
  ws.getRange(`A${row}`).format.font.bold = true;
}

export async function startFuncionesBuscarx(): Promise<void> {
  await Excel.run(async (context) => {
    await safeDeleteSheet(context, SHEETS.catalogo);
    await safeDeleteSheet(context, SHEETS.pedidos);
    await safeDeleteSheet(context, SHEETS.consultas);

    const catalogo = context.workbook.worksheets.add(SHEETS.catalogo);
    const pedidos = context.workbook.worksheets.add(SHEETS.pedidos);
    const consultas = context.workbook.worksheets.add(SHEETS.consultas);

    /* ── Catálogo ── */
    writeTitle(catalogo, "A1:F1", "Central de Pedidos", "Catálogo de productos");
    const catTable = catalogo.tables.add("A3:F3", true);
    catTable.name = "Catalogo";
    catTable.getHeaderRowRange().values = [[...CATALOGO_HEADERS]];
    catTable.rows.add(undefined, CATALOGO.map((row) => [...row]));
    catTable.style = "TableStyleMedium2";
    catTable.columns.getItem("Precio").getDataBodyRange().numberFormat =
      CATALOGO.map(() => ["$#,##0"]);
    catalogo.getRange("A:F").format.autofitColumns();

    /* ── Pedidos ── */
    writeTitle(pedidos, "A1:F1", "Central de Pedidos", "Registro de pedidos (enero–marzo 2026)");
    const pedTable = pedidos.tables.add("A3:F3", true);
    pedTable.name = "Pedidos";
    pedTable.getHeaderRowRange().values = [[...PEDIDOS_HEADERS]];
    pedTable.rows.add(
      undefined,
      PEDIDOS.map(([pedido, fecha, sku, cantidad, vendedor]) => [
        pedido,
        excelDate(fecha),
        sku,
        cantidad,
        vendedor,
        "",
      ])
    );
    pedTable.style = "TableStyleMedium2";
    pedTable.columns.getItem("Fecha").getDataBodyRange().numberFormat =
      PEDIDOS.map(() => ["dd/mm/yyyy"]);
    markAnswer(pedidos, `F4:F${LAYOUT.pedidosLastRow}`);
    pedidos.getRange("A:E").format.autofitColumns();
    pedidos.getRange("F:F").format.columnWidth = 160;

    /* ── Consultas ── */
    writeTitle(consultas, "A1:E1", "Central de Pedidos", "Consultas con BUSCARX — escribe tus fórmulas en las celdas amarillas");
    consultas.getRange("A3:C3").values = [["Tarea", "Dato", "Respuesta"]];
    consultas.getRange("A3:C3").format.font.bold = true;
    consultas.getRange("A3:C3").format.fill.color = "#2E5C9A";
    consultas.getRange("A3:C3").format.font.color = "#FFFFFF";

    labelRow(consultas, 4, "Tarea 1 · Precio del SKU", CONSULTAS.skuPrecio);
    labelRow(consultas, 6, "Tarea 3 · SKU que no existe", CONSULTAS.skuInexistente);
    labelRow(consultas, 8, "Tarea 4 · SKU del producto", CONSULTAS.productoParaSku);
    labelRow(consultas, 10, "Tarea 5 · Ficha del SKU", CONSULTAS.skuDetalle);

    const vRow = LAYOUT.vendedoresFirstRow;
    consultas.getRange(`A${vRow - 1}:C${vRow - 1}`).values = [
      ["Tarea 6 · Vendedor", "Ventas", "% Comisión"],
    ];
    consultas.getRange(`A${vRow - 1}:C${vRow - 1}`).format.font.bold = true;
    consultas.getRange(`A${vRow}:B${vRow + VENTAS_VENDEDORES.length - 1}`).values =
      VENTAS_VENDEDORES.map(([vendedor, ventas]) => [vendedor, ventas]);
    consultas.getRange(`B${vRow}:B${vRow + VENTAS_VENDEDORES.length - 1}`).numberFormat =
      VENTAS_VENDEDORES.map(() => ["$#,##0"]);
    consultas.getRange(`C${vRow}:C${vRow + VENTAS_VENDEDORES.length - 1}`).numberFormat =
      VENTAS_VENDEDORES.map(() => ["0%"]);

    labelRow(consultas, 20, "Tarea 7 · Texto a buscar", CONSULTAS.textoParcial);
    labelRow(consultas, 22, "Tarea 8 · Último pedido del SKU", CONSULTAS.skuUltimoPedido);
    consultas.getRange("C22").numberFormat = [["dd/mm/yyyy"]];
    labelRow(consultas, 24, "Tarea 9 · Producto", CONSULTAS.productoRegion);
    labelRow(consultas, 25, "Tarea 9 · Región", CONSULTAS.region);
    consultas.getRange("C4").numberFormat = [["$#,##0"]];
    consultas.getRange("C24").numberFormat = [["$#,##0"]];

    for (const [taskId, cell] of Object.entries(ANSWER_CELLS)) {
      if (cell.sheet !== SHEETS.consultas) continue;
      if (Number(taskId) === 6) {
        markAnswer(consultas, `C${vRow}:C${vRow + VENTAS_VENDEDORES.length - 1}`);
      } else if (Number(taskId) === 5) {
        markAnswer(consultas, "C10:E10");
      } else {
        markAnswer(consultas, cell.address);
      }
    }

    // Commission brackets (G3) and regional price matrix (G11), used by tasks 6 and 9.
    const comTable = consultas.tables.add(
      `G3:H${3 + TRAMOS_COMISION.length}`,
      true
    );
    comTable.name = "Comisiones";
    comTable.getHeaderRowRange().values = [["Desde ventas", "% comisión"]];
    comTable.getDataBodyRange().values = TRAMOS_COMISION.map(([desde, pct]) => [desde, pct]);
    comTable.columns.getItem("Desde ventas").getDataBodyRange().numberFormat =
      TRAMOS_COMISION.map(() => ["$#,##0"]);
    comTable.columns.getItem("% comisión").getDataBodyRange().numberFormat =
      TRAMOS_COMISION.map(() => ["0%"]);
    comTable.style = "TableStyleLight9";

    const regionLast = 11 + PRECIOS_REGION.length;
    consultas.getRange(`G11:J11`).values = [["Producto", ...REGIONES]];
    consultas.getRange(`G11:J11`).format.font.bold = true;
    consultas.getRange(`G11:J11`).format.fill.color = "#DDEBF7";
    consultas.getRange(`G12:J${regionLast}`).values = PRECIOS_REGION.map((row) => [...row]);
    consultas.getRange(`H12:J${regionLast}`).numberFormat = PRECIOS_REGION.map(() => [
      "$#,##0",
      "$#,##0",
      "$#,##0",
    ]);
    consultas.getRange("G10").values = [["Precios por región"]];
    consultas.getRange("G10").format.font.bold = true;

    consultas.getRange("A:A").format.columnWidth = 190;
    consultas.getRange("B:B").format.columnWidth = 120;
    consultas.getRange("C:E").format.columnWidth = 120;
    consultas.getRange("G:J").format.autofitColumns();

    consultas.activate();
    consultas.getRange("C4").select();
    await context.sync();
  });
}
